import { isInstalledPwa } from './offline-display-mode';

// Offline mode is available to every school regardless of plan tier — not gated by the
// FeatureGroups/accountfeatures plan-feature system (explicit user direction). The only gate
// here is the per-school on/off toggle (schools.OfflineModeEnabled), which a school's own
// admin controls directly, independent of billing.
//
// DELIBERATELY NOT AN INSTALLED-APP CHECK, and it must not become one again. This function's
// biggest job is deciding WHICH offline screen a user sees when the network drops
// (app.component.ts's offlineBannerMode): a student gets the translucent overlay with a link
// through to their downloads, an admin gets the opaque "check your internet connection" wall.
// That is an admin-vs-student distinction (D5) with nothing to do with how the app was
// launched. Folding an install check in here turned every student into the admin case and cut
// them off from content already sitting on their device.
export function isOfflineModeEnabled(schoolToggle: boolean): boolean {
  return !!schoolToggle;
}

// Whether to REFUSE saving outside the installed app.
//
// Deliberately off for now. Restricting ordinary browser tabs is a browser-experience
// decision, and the sequencing is to get the installed-app flow working end to end first
// (install from the home screen → save a course → lose the network → land on the downloads
// screen → open a course → play a video embedded in its notes). Leaving this on while that
// is being tested means one wrong answer from isInstalledPwa() — an unexpected display-mode,
// or a manifest the browser declined so "add to home screen" produced a plain shortcut
// instead of a real install — silently removes the save control and looks exactly like the
// feature being broken. That is a bad thing to be debugging through.
//
// Turning it on is this one line, once the installed app is confirmed to be recognised.
const REQUIRE_INSTALLED_APP_TO_SAVE = false;

// Whether this session may SAVE new content for offline use.
//
// Note which half of the feature this guards: acquiring content, never reading it back.
// Reading and playback are not install-gated anywhere, and should not become so. A false
// negative from isInstalledPwa() on the save path hides a control — visible, and fixed by
// installing. The same false negative on the read path would strip a student of downloads
// they already hold, with no route to recovering them.
export function canSaveForOffline(schoolToggle: boolean): boolean {
  if (!isOfflineModeEnabled(schoolToggle)) return false;
  return !REQUIRE_INSTALLED_APP_TO_SAVE || isInstalledPwa();
}
